// GET /cartera-dashboard/groups and /cartera-dashboard/groups/:id/assets
// (ADR-0072) — the desktop gerencial "Proyectos y Unidades" surface.
//
// Deliberately a separate wire module from asset-groups.ts, not an
// extension of it: /asset-groups serves the mobile cartera (any
// authenticated org member, ADR-0043) and resolves its strip-cifra label
// from `recipe.group.assetCountLabel` ("propiedades" for postventa) —
// both wrong for this surface, which needs the MANAGEMENT_ANALYTICS_ROLE_KEYS gate
// and the "Unidades" label from `recipe.labels.assetPlural`. This module
// carries `labels` at the response root instead of per-row, so the wrong
// label is structurally unreachable rather than patched around.
import { assetGroupIdSchema, assetSchema } from "@bitacorapro/core-engine";
import { isoDateTimeSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { latestReportSummarySchema } from "./asset-groups.js";
import { unitDeliveryWireSchema } from "./unit-delivery.js";
export const carteraDashboardParams = z.object({ id: assetGroupIdSchema });
export const carteraDashboardLabelsSchema = z.object({
    // recipe.labels.assetGroupPlural — "Proyectos".
    groupPlural: z.string().min(1),
    // recipe.labels.assetGroup — "Proyecto".
    group: z.string().min(1),
    // recipe.labels.assetPlural — "Unidades". Never recipe.group.assetCountLabel.
    assetPlural: z.string().min(1),
    // recipe.labels.asset — "Unidad".
    asset: z.string().min(1),
});
// One row on the Proyectos list. Aggregate-only by design (ADR-0072,
// scalability-engineer) — no per-asset rows at this level, so the list
// stays cheap regardless of how many unidades a proyecto holds. Full
// unidad rows live behind the paginated detail route below.
export const carteraDashboardGroupItemSchema = z.object({
    id: assetGroupIdSchema,
    name: z.string().min(1).max(255),
    subline: z.string().optional(),
    assetCount: z.number().int().nonnegative(),
    openObservations: z.number().int().nonnegative(),
    // Most recent signed acta (any reportType, any unidad) in the proyecto.
    // Null when the proyecto has no signed actas yet.
    lastActivityAt: isoDateTimeSchema.nullable(),
    // Max `recipe.severities[].rank` across the proyecto's open
    // observations. Null when there are none. FE derives the semáforo
    // color from this rank against `recipe.severities` — never from a
    // severity-key string comparison.
    maxOpenSeverityRank: z.number().int().nullable(),
    // Los tres números que la card del proyecto pide, más el contador de
    // unidades activas. Opcionales porque sólo tienen sentido para una
    // vertical con ciclo de observaciones y con estados de activación: la
    // cartera de facility no declara ninguno de los dos, y un cero ahí
    // sería una afirmación falsa en vez de una ausencia.
    //
    // «Activa» se resuelve contra `activationAssetStatus` de la receta, no
    // contra el literal `en_garantia` — hay una fitness que prohíbe ese
    // literal en core-api.
    activeAssetCount: z.number().int().nonnegative().optional(),
    /** «Por aceptar»: fase `triage` de `observationPhaseKeys` SIN las
     *  reabiertas (ciclo ≤ 1 / NULL) — lo que la inmobiliaria todavía tiene
     *  que aceptar o rechazar por primera vez. Las reabiertas por «No
     *  conforme» (ciclo ≥ 2) viven sólo en `unassignedObservations`. Las
     *  APELADAS (ADR-0141, `rechazo_apelado`) no están en ninguno de los dos:
     *  el jefe sólo las acoge o las mantiene. Así
     *  `pendingTriage + unassignedObservations + apeladas ===
     *  pendingObservations` (las apeladas = la diferencia). */
    pendingTriage: z.number().int().nonnegative().optional(),
    /** «Por asignar» (regla del founder): la fase `awaiting_assignment`
     *  MÁS las reabiertas (triage ∧ ciclo ≥ 2), que vuelven a necesitar
     *  contratista aunque el anterior siga en `provider_id`. Sin condición
     *  sobre `provider_id`. Es el trabajo que le toca al jefe de proyecto
     *  AHORA, y por eso va en la card. */
    unassignedObservations: z.number().int().nonnegative().optional(),
    /** Lo que el proyecto tiene esperando a la inmobiliaria: triage ∪
     *  «por asignar», deduplicado — el mismo predicado que el
     *  `pendingObservations` por unidad. Incluye las apeladas, así que es
     *  `pendingTriage + unassignedObservations` MÁS las apeladas; el
     *  cliente lo lee en vez de sumar. */
    pendingObservations: z.number().int().nonnegative().optional(),
    /** Fase `with_assignee`: el trabajo está en manos del contratista. */
    withAssignee: z.number().int().nonnegative().optional(),
    /** En el estado desde el cual se da la conformidad, sin overlay de
     *  cierre: esperan al propietario, no a la inmobiliaria. */
    awaitingConformity: z.number().int().nonnegative().optional(),
    /** Última actividad de OBSERVACIONES del proyecto, distinta de
     *  `lastActivityAt`, que es la última acta firmada. Un proyecto
     *  entregado hace un año puede tener movimiento de postventa de hoy, y
     *  al revés — la card muestra ésta. */
    lastObservationAt: isoDateTimeSchema.nullable().optional(),
    /** ADR-0145 — consultas entregadas del propietario de este proyecto
     *  sin responder la última pregunta del propietario (`email_sent =
     *  true` y `answered_at IS NULL`, ADR-0155: una consulta respondida a la
     *  que el propietario vuelve a escribir cuenta de nuevo), con el mismo
     *  alcance que los demás conteos.
     *  Es el badge del rail «Consultas»; se calcula con las vencidas
     *  incluidas (vencida ⊂ sin responder). Opcional: sólo se emite para una
     *  receta con canal del propietario (asset_party externo, hoy
     *  postventa); facility no lo emite aunque tenga ciclo de observaciones. */
    unansweredInquiries: z.number().int().nonnegative().optional(),
    createdAt: isoDateTimeSchema,
});
export const carteraDashboardGroupsQuery = z
    .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    q: z.string().trim().min(1).max(120).optional(),
})
    .strict();
export const carteraDashboardGroupsResponse = z.object({
    labels: carteraDashboardLabelsSchema,
    items: z.array(carteraDashboardGroupItemSchema),
    nextCursor: z.string().nullable(),
});
// One row on a proyecto's Unidades detail table.
export const carteraDashboardAssetItemSchema = assetSchema
    .pick({ id: true, address: true, unit: true, assetGroupId: true })
    .extend({
    // recipe.labels.assetOwnerSection-scoped attribute, resolved
    // server-side — e.g. "Propietario" subline. Undefined when the
    // recipe has no such field configured.
    ownerLabel: z.string().optional(),
    // Estado actual de la unidad (`assets.attributes.asset_status`),
    // resuelto server-side contra `recipe.assetStatuses`. Null cuando la
    // vertical no usa el campo. Es lo que gobierna si la unidad puede
    // recibir observaciones (ADR-0065 enmendado,
    // `observationsRequireAssetStatusIn`), así que el panel puede
    // distinguir "no activada" de "activada" sin inferirlo del acta.
    assetStatus: z.string().nullable(),
    // DEPRECADO (2026-09-12). Estado de firma del reportType marcado
    // `milestone` (postventa: "acta_entrega"). Dejó de ser la señal de
    // "esta unidad puede recibir observaciones" —eso es `assetStatus`
    // ahora— y se mantiene un ciclo sólo para no romper clientes
    // desplegados. Nada nuevo debe leerlo.
    deliveryActaSignedAt: isoDateTimeSchema.nullable(),
    latestReport: latestReportSummarySchema.nullable(),
    openObservations: z.number().int().nonnegative(),
    /** Lo que la unidad tiene esperando a la inmobiliaria: triage ∪
     *  «por asignar» (mismos predicados que la card del proyecto).
     *  Sólo viaja cuando la receta declara ciclo de observaciones. */
    pendingObservations: z.number().int().nonnegative().optional(),
    /** F9 (corte 8) — la entrega programada de la ficha
     *  (`fecha_entrega`, ADR-0144) en hora de pared de Santiago; null sin
     *  fecha. Sólo viaja cuando la receta entrega unidades a una
     *  contraparte EXTERNA (postventa); facility no lo emite. */
    entrega: unitDeliveryWireSchema.nullable().optional(),
    /** F9 — ¿la unidad tiene un propietario con cuenta? Sólo a él se le
     *  avisa la entrega. Mismo gate que `entrega`. */
    ownerHasAccount: z.boolean().optional(),
});
export const carteraDashboardAssetsQuery = z
    .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
export const carteraDashboardAssetsResponse = z.object({
    labels: carteraDashboardLabelsSchema,
    group: carteraDashboardGroupItemSchema,
    items: z.array(carteraDashboardAssetItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=cartera-dashboard.js.map